import DocumentRepository from "../domain/data/DocumentRepository";
import Campaign from "../domain/entities/Campaign";
import Document from "../domain/entities/Document";
import Signatory from "../domain/entities/Signatory";
import User from "../domain/entities/User";
import { SendDocFormInputs } from "../presentation/components/widgets/SendIndvDocumentForm";
import AwsApiClient from "../presentation/hooks/AwsApiClient";

export default class APIDocumentRepository implements DocumentRepository {
  private client = new AwsApiClient();

  async saveDocument(document: Document): Promise<Document> {
    try {
      const response = await this.client.postExternal(
        `/api/document`,
        document
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveDocumentRecord(document: Document): Promise<Document> {
    try {
      const response = await this.client.postExternal(
        `/api/document/saverecord`,
        document
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveDocumentAction(document: Document): Promise<Document> {
    try {
      const response = await this.client.postExternal(
        `/api/document/saveaction`,
        document
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async sendDocumentIndividual(formData: SendDocFormInputs): Promise<boolean> {
    try {
      const response = await this.client.post(`/api/campaign/oneoff`, formData);
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getDocument(
    organizationId: string,
    documentId: string
  ): Promise<Document | undefined> {
    try {
      try {
        const response = await this.client.get(
          `/api/document/${organizationId}/${documentId}`
        );
        return response;
      } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
      }
    } catch (error) {
      return undefined;
    }
  }

  async getDocumentExt(shortId: string): Promise<Document | undefined> {
    try {
      try {
        const response = await this.client.getExternal(
          `/api/ext/document/${shortId}`
        );
        return response;
      } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
      }
    } catch (error) {
      return undefined;
    }
  }
  
  async getDocuments(user: User, campaign: Campaign): Promise<Document[]> {
    throw new Error("Method not implemented.");
  }
  
  async processDocumentStaffbyCampaign(campaign: Campaign): Promise<Document> {
    try {
      const response = await this.client.post(
        `/api/document/saveStaff`, campaign
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getSignatoryDocuments(
    organizationId: string,
    signatoryId: string,
    downloadFile: boolean
  ): Promise<Document[]> {
    try {
      const response = await this.client.getExternal(
        `/api/documents/file/${organizationId}/${signatoryId}/${downloadFile}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getRequestorDocuments(
    organizationId: string,
    requestorId: string
  ): Promise<Document[]> {
    try {
      const response = await this.client.get(
        `/api/documents/requestor/${organizationId}/${requestorId}`
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getDocuments2(
    user: User | Signatory,
    campaign?: Campaign | unknown
  ): Promise<Document[]> {
    throw new Error("Method not implemented.");
  }

  async signDocument(
    user: User | Signatory,
    organizationId: string,
    documentId: string,
    signedDocument: string,
    formData: string
  ): Promise<string | undefined> {
    try {
      const response = await this.client.post(
        `/api/document/savesignature/${organizationId}/${documentId}`,
        {
          signaturePositionFile: signedDocument,
          formData,
        },
        "text"
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async externalSignDocument(
    shortId: string,
    signedDocument: string,
    formData: string
  ): Promise<string | undefined> {
    try {
      const response = await this.client.postExternal(
        `/api/ext/document/savesignature/${shortId}`,
        {
          signaturePositionFile: signedDocument,
          formData,
        },
        "text"
      );
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async resendDocument(
    user: User,
    documentId: string
  ): Promise<{ documentId: string; success: boolean; message?: string }> {
    try {
      const response = await this.client.post(
        `/api/document/send/${user.organization.id}/${user.id}/${documentId}`, null, "none"
      );
      const success = response?.success ?? true;
      return { documentId, success, message: response?.message };
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async sendBackDocument(
    user: User,
    documentId: string,
    reason: string
  ): Promise<boolean> {
    try {
      const response = await this.client.post(
        `/api/document/sendback/${user.organization.id}/${user.id}/${documentId}`, 
        {reason},
        "none"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async saveDocumentProgress(
    organizationId: string,
    documentId: string,
    attachmentsString: string,
    signature: string
  ): Promise<boolean> {
    try {
      const response = await this.client.postExternal(
        `/api/document/saveprogress/${organizationId}/${documentId}`, 
        {
          attachmentsString,
          signaturePositionFile: signature
        },
        "none"
      );
      return true;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async downloadPDF(
    organizationId: string,
    userId: string,
    documentId: string
  ): Promise<string> {
    try {
      const response = await this.client.post(
        `/api/document/esign/${organizationId}/${userId}/${documentId}`, null, "text"
      );
      if (response === null || response === "") {
        throw new Error("PDF not obtained");
      }
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async downloadPDFwithAuditSummary(
    organizationId: string,
    userId: string,
    documentId: string
  ): Promise<{ signedDocumentPdf: string; auditSummaryPdf: string }> {
    try {
      const response = await this.client.post(
        `/api/document/esign/audit/${organizationId}/${userId}/${documentId}`, null
      );
      if (response === null || response === "") {
        throw new Error("PDF not obtained");
      }
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async translateText(text: string, targetLanguage: string): Promise<any> {
    try {
      const response = await this.client.postExternal(
        `/api/document/translateText`,
        { text, targetLanguage }
      );
      return response;
    } catch (error) {
      const message = `An error has occurred: ${error}`;
      throw new Error(message);
    }
  }

  async saveAttachment(
    documentId: string,
    attachmentId: string,
    attachmentFile: string
  ): Promise<string> {
    try {
      const response = await this.client.postExternal(
        `/api/document/attachment/${documentId}/${attachmentId}`, 
        {attachmentFile},
        "text"
      );
      return attachmentId;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }

  async getAttachment(
    documentId: string,
    attachmentId: string
  ): Promise<string> {
    try {
      const response = await this.client.getExternal(
        `/api/document/attachment/${documentId}/${attachmentId}`, "text"
      );
      if (response === null || response === "") {
        throw new Error("PDF not obtained");
      }
      return response;
    } catch (error) {
      const message = `An error has occured: ${error}`;
      throw new Error(message);
    }
  }
}
